/**
 * Overlays: a centred modal sheet, the confirmation dialog for destructive
 * actions, and a mount fade for inline notices.
 *
 * Both dialogs render through `@rn-primitives/portal`, so the app mounts
 * `<PortalHost />` once near its root and they stack above the page without
 * knowing where in the tree they were declared. Each takes a `className` merged
 * last, and none of them reads the appearance mode: the surfaces are token
 * classes, so dark mode follows the `dark` class on the document root.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { ActivityIndicator, Animated, Pressable, ScrollView, View } from 'react-native';

import * as DialogPrimitive from '@rn-primitives/dialog';

import { Button } from './ui/button';
import { Dialog, DialogDescription, DialogOverlay, DialogPortal, DialogTitle } from './ui/dialog';
import { Text } from './ui/text';
import { Row, Stack } from './layout';
import { useResponsive } from '../lib/hooks';
import { useKeyboardInset, useRevealFocusedField } from '../lib/keyboard';
import { useAppearance } from '../providers/appearance';
import { cn } from '../lib/utils';

/**
 * The overlay's own padding, matching its `p-4`. Kept here so the keyboard's
 * inset can be added on top of it rather than replacing it.
 */
const OVERLAY_PADDING = 16;

/**
 * A centred modal sheet.
 *
 * One component serves a phone confirmation and an iPad detail panel: the sheet
 * is capped by `maxWidth` and by a share of the viewport, so the same screen
 * fits both. The body scrolls while the header and footer stay put, which keeps
 * the primary action reachable however long the content is.
 *
 * @param props Visibility, dismiss handler, title, and content.
 * @returns The modal sheet, rendered in the portal host.
 */
export function Modal({
  visible,
  onClose,
  title,
  subtitle,
  children,
  footer,
  maxWidth = 560,
  dismissable = true,
  square = false,
  exactWidth = false,
  placement = 'center',
}: {
  visible: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Widest the sheet grows, in points. */
  maxWidth?: number;
  /** Whether Escape, a backdrop press, or the close control dismisses it. */
  dismissable?: boolean;
  /**
   * Gives the sheet `maxWidth` as an exact width rather than a ceiling.
   *
   * What square content needs in order to be square. `aspect-square` resolves
   * against the width a box is actually given, and a sheet sized by its contents
   * hands its children whatever the widest one needs — so the width has to be
   * stated before anything inside can be squared against it. The caller squares
   * its own content; this only makes the room definite.
   */
  square?: boolean;
  /**
   * Gives the sheet `maxWidth` as an exact width rather than a ceiling.
   *
   * A centred sheet is sized by its contents: nothing inside is as wide as the
   * sheet is allowed to be, so it comes out as wide as its widest row. A panel
   * laid out in columns needs the width stated up front, and so does one that
   * simply reads better with room. `square` implies this.
   */
  exactWidth?: boolean;
  /**
   * Where the sheet sits. `bottom` anchors it to the bottom edge, full width and
   * square at the top — a sheet for a phone, opened from a bar at the top of the
   * screen. `maxWidth` and `square` are ignored there: a bottom sheet is as wide
   * as the screen by definition, and centring it would leave it floating.
   */
  placement?: 'center' | 'bottom';
}) {
  const keyboard = useKeyboardInset();
  const { height: viewportHeight, width: viewportWidth } = useResponsive();
  const isBottom = placement === 'bottom';

  // A dialog renders through a portal, so it is not inside a `Screen` and would
  // not otherwise see the keyboard at all. Without this the sheet stays centred
  // in the full layout viewport while the keyboard covers its lower half — the
  // footer buttons, which is where the action is.
  useRevealFocusedField(keyboard.open, keyboard.height);

  // A sheet taller than the viewport would run off both ends, putting its title
  // and its actions out of reach at once. The body scrolls inside this cap.
  const maxHeight = Math.round((keyboard.open ? keyboard.visibleHeight : viewportHeight) * 0.9);

  // A square is bounded by the viewport as well as by `maxWidth`, or it would
  // overflow a phone held in portrait.
  const side = square ? Math.min(maxWidth, viewportWidth - OVERLAY_PADDING * 2, maxHeight) : undefined;
  // A centred sheet given no width is as wide as its contents, which is rarely
  // as wide as it was allowed to be.
  const fixedWidth =
    side ?? (exactWidth ? Math.min(maxWidth, viewportWidth - OVERLAY_PADDING * 2) : undefined);

  // A bottom sheet sits on the screen's own edge, so it adds no padding of its
  // own for the keyboard inset to sit on top of.
  const overlayPadding = isBottom ? 0 : OVERLAY_PADDING;

  return (
    <Dialog
      open={visible}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}>
      <DialogPortal>
        <DialogOverlay
          className={cn(
            'bg-black/50 dark:bg-black/80',
            // The overlay is a column (React Native Web's default direction), so
            // the axes read the other way round from a plain `div`: `justify`
            // places the sheet vertically and `align-items` horizontally. Getting
            // that backwards is what put the first version of this sheet in the
            // bottom-right corner as a narrow column.
            isBottom ? 'items-center justify-end p-0' : 'p-4',
          )}
          // The backdrop stays full-bleed and the keyboard's height is added as
          // bottom padding instead. Shortening the overlay moves its edge up the
          // screen and leaves everything below it undimmed — obvious in
          // landscape, and plainly wrong with an iPad's floating or split
          // keyboard, which does not reach the bottom of the display. Padding
          // lifts the sheet just the same, because the overlay centres its
          // children in the content box.
          style={
            keyboard.open
              ? { paddingBottom: keyboard.height + overlayPadding }
              : undefined
          }
          // A backdrop press is the pointer's dismiss gesture; a blocking
          // dialog must not honour it.
          onPress={(event) => {
            if (!dismissable) event.preventDefault();
          }}>
          <DialogPrimitive.Content
            // Escape and a press outside the sheet both dismiss it, so both
            // must be refused when the sheet is blocking.
            onEscapeKeyDown={(event) => {
              if (!dismissable) event.preventDefault();
            }}
            onInteractOutside={(event) => {
              if (!dismissable) event.preventDefault();
            }}
            // Both dimensions are measured point values, which a class name
            // cannot carry. The height cap in particular cannot be a percentage:
            // this element's parent is the dialog wrapper, whose height is sized
            // by its own content, and CSS ignores a percentage against an
            // indefinite containing block. A long form therefore grew past the
            // viewport and out from under the keyboard at both ends.
            style={
              isBottom
                ? {
                    // A bottom sheet is the width of the screen, and like
                    // `maxWidth` below that width has to be measured rather than
                    // asked for with a class. `w-full` resolves against this
                    // element's parent, which is the dialog wrapper — a box sized
                    // by its own contents — so the declaration collapses to
                    // whatever the panel happens to contain and the sheet renders
                    // as a narrow right-hand column instead of a sheet.
                    //
                    // Callers are expected to reserve this placement for phones:
                    // on a window wider than a phone the same panel belongs in a
                    // centred dialog, which is a decision `Modal` cannot make for
                    // itself because only the caller knows what the panel is.
                    width: viewportWidth,
                    maxHeight,
                  }
                : {
                    // A square needs a definite width as well as a definite height:
                    // a sheet sized by its contents comes out as wide as its widest
                    // child, so the body beside it would be narrower than it is tall.
                    ...(fixedWidth === undefined
                      ? { maxWidth }
                      : { width: fixedWidth, maxWidth: fixedWidth }),
                    maxHeight,
                  }
            }
            className={cn(
              'bg-popover border-border z-50 w-full overflow-hidden border shadow-lg shadow-black/5',
              isBottom ? 'rounded-t-2xl' : 'rounded-xl',
            )}>
            {isBottom ? (
              // The grabber is decoration, not a handle: the sheet dismisses on
              // a backdrop press and on Escape, and both work without it. It is
              // here because a panel that reaches the bottom edge reads as
              // dragged up rather than as a centred dialog that fell over.
              <View className="bg-border mx-auto mt-2 h-1 w-10 rounded-full" />
            ) : null}

            {title ? (
              <Row
                spread
                gap="md"
                // With a subtitle the header is two lines and the close control
                // belongs beside the first of them; with only a title it belongs
                // on the title's own centre line, which is where it reads as part
                // of the title rather than as something floating above it.
                align={subtitle ? 'start' : 'center'}
                className="p-4">
                <Stack gap="xs" className="flex-1">
                  <DialogTitle>{title}</DialogTitle>
                  {subtitle ? <DialogDescription>{subtitle}</DialogDescription> : null}
                </Stack>
                {dismissable ? (
                  <Pressable
                    onPress={onClose}
                    accessibilityLabel="Close"
                    role="button"
                    hitSlop={12}
                    // No ring on this one, and that is deliberate. React Native
                    // Web renders `role="button"` as a real `<button>`, and this
                    // is the first tabbable in the sheet — so Radix focuses it
                    // every time a dialog opens, and iOS counts that programmatic
                    // focus as `:focus-visible`. A ring here is therefore drawn
                    // unasked on the one control that exists to be dismissed, and
                    // reads as a box around the × rather than as focus.
                    //
                    // The glyph carries the state instead: it darkens when the
                    // ring would have appeared, so a keyboard user still sees
                    // where they are without a border appearing on its own. The
                    // group is what lets the text react to the button's focus —
                    // `Text` sets its own colour, so it cannot inherit one.
                    className="group rounded-md outline-none">
                    {/* `leading-none` keeps the glyph's box the size of the glyph.
                        Left at the default the line box is 28pt against the
                        title's 18pt, and the extra leading hung below the line it
                        is supposed to sit on — five points of it, which is enough
                        to read as misaligned. */}
                    <Text className="text-subtle-foreground group-focus-visible:text-foreground text-xl font-semibold leading-none">
                      ×
                    </Text>
                  </Pressable>
                ) : null}
              </Row>
            ) : null}

            <ScrollView
              className="grow-0"
              // The body drops its top padding only when a header sits above it
              // to provide that gap. Applied unconditionally, a sheet without a
              // title — the print preview — had its content flush to the top edge.
              contentContainerClassName={cn(
                'p-4',
                title && 'pt-0',
                // The body's own 16 points below its last row read as tighter
                // than the rhythm inside the panel: its sections are spaced 24
                // apart, so the edge looked as though the content had been cut
                // short against it — as much in a centred dialog as on a sheet,
                // where the edge is the screen itself. 24 matches that spacing.
                // The sides keep the body's own padding: it is the distance to
                // the content below that the eye compares with the gaps above it.
                'pb-6',
              )}>
              {children}
            </ScrollView>

            {footer ? <View className="border-border border-t p-4">{footer}</View> : null}
          </DialogPrimitive.Content>
        </DialogOverlay>
      </DialogPortal>
    </Dialog>
  );
}

/**
 * A confirmation dialog for destructive actions.
 *
 * Built on `Modal` rather than on an alert dialog so that a backdrop press
 * cancels, which is what this dialog has always done. An alert dialog refuses
 * outside presses by design, and quietly taking that away would change a
 * gesture customers and merchants already rely on.
 *
 * @param props Visibility, copy, and handlers.
 * @returns The confirmation dialog.
 */
export function ConfirmDialog({
  visible,
  title,
  message,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive,
  confirmVariant,
  busy,
  onConfirm,
  onCancel,
}: {
  visible: boolean;
  title: string;
  message: string;
  /**
   * Anything the confirmation owes beyond the sentence.
   *
   * A batch action usually has one more fact to state than it has room for in a
   * single message — what else the same tap will reach. It goes under the
   * message so the dialog stays one component rather than two.
   */
  children?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  /** The confirm button's own treatment. `destructive` is the shorthand for its red. */
  confirmVariant?: 'default' | 'destructive' | 'success';
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const variant = confirmVariant ?? (destructive ? 'destructive' : 'default');
  const { palette } = useAppearance();

  return (
    <Modal
      visible={visible}
      onClose={onCancel}
      title={title}
      maxWidth={440}
      footer={
        <Row gap="sm" justify="end">
          <Button variant="secondary" onPress={onCancel} disabled={busy}>
            <Text>{cancelLabel}</Text>
          </Button>
          {/* The label stays while it works. This button is sized by its contents, so
              swapping the words for an indicator collapsed it to a pill and jumped the
              footer sideways — and an indicator that cannot say what it is doing reads
              as broken rather than as busy. The spinner sits beside the words instead,
              in the button's own foreground, which is how the rest of the app shows a
              button at work (`ActionButton`, the board's hand-over button). */}
          <Button
            variant={variant}
            onPress={onConfirm}
            disabled={busy}
            aria-busy={Boolean(busy)}>
            {busy ? <ActivityIndicator size="small" color={palette.primaryForeground} /> : null}
            <Text>{confirmLabel}</Text>
          </Button>
        </Row>
      }>
      <Stack gap="md">
        <Text className="text-muted-foreground text-base">{message}</Text>
        {children}
      </Stack>
    </Modal>
  );
}

/**
 * Fades its children in on mount.
 *
 * For inline notices that appear in response to an action: a notice that snaps
 * into place reads as a layout jump, while a fade reads as something arriving.
 *
 * @param props Content and the transition length in milliseconds.
 * @returns The fading wrapper.
 */
export function FadeIn({ children, duration = 220 }: { children: ReactNode; duration?: number }) {
  const opacity = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(opacity, { toValue: 1, duration, useNativeDriver: true }).start();
  }, [opacity, duration]);

  return <Animated.View style={{ opacity }}>{children}</Animated.View>;
}
